import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Collapse, DatePicker, Form, Input, Row, Select, Switch, Typography } from 'antd';
import { PlusOutlined, SaveOutlined } from '@ant-design/icons';
import FactoryFormModal from '../../../components/factories/FactoryFormModal';
import type { FactoryInput } from '../../../dto/factory';
import { createFactory } from '../../reference-data/api/referenceDataApi';
import { getApiErrorMessage } from '../../../utils/apiError';
import { useFeedback } from '../../../utils/feedback';
import { useTranslation } from 'react-i18next';
import type { FactoryOptionDto } from '../../../dto/reference';
import { supplierOrderNumberRule } from '../../../dto/supplierOrder';
import type { SupplierOrderMoreDataValues } from '../supplierOrderDetailEditUtils';

const { Text } = Typography;
const DATE_FORMATS = ['DD.MM.YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'];

interface SupplierOrderMoreDataSectionProps {
  values: SupplierOrderMoreDataValues;
  factories: FactoryOptionDto[];
  canEdit: boolean;
  /** New ZD: the values stay in the page and go out with the first save. */
  onChange?: (values: SupplierOrderMoreDataValues) => void;
  /** Saved draft: „Zapisz dane” saves the values at once (PUT). */
  /** Resolves true when the values were saved (the section then follows the saved ones again). */
  onSave?: (values: SupplierOrderMoreDataValues) => Promise<boolean>;
  saving?: boolean;
  /** Number the backend will give a new ZD with automatic numbering (preview). */
  autoNumberPreview?: string | null;
  /** „Dodaj fabrykę” (kept from the former ZD wizard): the page adds it to its list. */
  onFactoryCreated?: (factory: FactoryOptionDto) => void;
}

/**
 * „Więcej danych” of the ZD card in stage „Szkic” (karta ZD według makiety,
 * 2026-10-05). The mockup shows only the supplier, lines and prices; the
 * fields of the former ZD wizard stay here, collapsed: factory, order date,
 * numbering, planned ready date, supplier reference, proforma number, notes.
 * Unit conversions are part of „Pozycje i ceny”.
 */
const SupplierOrderMoreDataSection: React.FC<SupplierOrderMoreDataSectionProps> = ({
  values, factories, canEdit, onChange, onSave, saving = false, autoNumberPreview, onFactoryCreated,
}) => {
  const { t } = useTranslation(['orders', 'common']);
  const feedback = useFeedback();
  const [form] = Form.useForm<SupplierOrderMoreDataValues>();
  const [factoryOpen, setFactoryOpen] = useState(false);
  const [factorySubmitting, setFactorySubmitting] = useState(false);

  const addFactory = async (payload: FactoryInput) => {
    setFactorySubmitting(true);
    try {
      const factory = await createFactory(payload as unknown as Record<string, unknown>);
      feedback.success(t('orders:supplierOrders.messages.factoryCreateSuccess', { factoryName: factory.name }));
      onFactoryCreated?.(factory);
      form.setFieldValue('factoryId', factory.id);
      onChange?.(form.getFieldsValue(true));
      setFactoryOpen(false);
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.messages.factoryCreateError'));
    } finally {
      setFactorySubmitting(false);
    }
  };
  const useManualNumbering = Form.useWatch('useManualNumbering', form);

  // Unsaved edits are never overwritten by a refresh of the card (e.g. after
  // „Pozycje i ceny” or the supplier choice); they follow the saved values again
  // once saved.
  const edited = useRef(false);
  const savedValuesKey = onSave ? JSON.stringify(values) : '';
  useEffect(() => {
    // Saved draft: re-seed when the saved values change (after a refresh), unless
    // the operator has unsaved edits. A new ZD owns its values in this form
    // (initialValues), so no re-seed.
    if (savedValuesKey && !edited.current) form.setFieldsValue(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, savedValuesKey]);

  const save = async () => {
    if (!onSave) return;
    try {
      await form.validateFields();
    } catch {
      return;
    }
    if (await onSave(form.getFieldsValue(true))) edited.current = false;
  };

  const content = (
    <Form
      form={form}
      layout="vertical"
      disabled={!canEdit}
      initialValues={values}
      onValuesChange={(_, all) => { edited.current = true; onChange?.(all); }}
      data-testid="supplier-order-more-data-form"
    >
      <Row gutter={[20, 0]}>
        <Col xs={24} md={12} lg={8}>
          <Form.Item
            name="factoryId"
            label={t('orders:supplierOrders.form.factory')}
            extra={canEdit ? (
              <Button type="link" size="small" icon={<PlusOutlined />} style={{ paddingInline: 0 }} onClick={() => setFactoryOpen(true)}>
                {t('orders:supplierOrders.form.addFactory')}
              </Button>
            ) : null}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('orders:supplierOrders.card.more.factoryPlaceholder')}
              options={factories.map((factory) => ({ value: factory.id, label: factory.name }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Form.Item name="orderDate" label={t('orders:supplierOrders.form.orderDate')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format={DATE_FORMATS} allowClear={false} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Form.Item
            name="expectedDelivery"
            label={t('orders:supplierOrders.form.expectedDelivery')}
            extra={t('orders:supplierOrders.card.more.expectedDeliveryHint')}
          >
            <DatePicker style={{ width: '100%' }} format={DATE_FORMATS} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Form.Item name="supplierReference" label={t('orders:supplierOrders.form.supplierReference')}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Form.Item name="proformaNumber" label={t('orders:supplierOrders.form.proformaNumber')}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Form.Item label={t('orders:supplierOrders.form.useManualNumbering')} style={{ marginBottom: 8 }}>
            <Form.Item name="useManualNumbering" valuePropName="checked" noStyle>
              <Switch aria-label={t('orders:supplierOrders.form.useManualNumbering')} />
            </Form.Item>
          </Form.Item>
          {useManualNumbering ? (
            <Form.Item
              name="manualOrderNumber"
              label={t('orders:supplierOrders.form.manualOrderNumber')}
              rules={[{
                required: true,
                whitespace: true,
                message: t('orders:modal.validation.manualNumberRequired'),
              }, supplierOrderNumberRule(t)]}
            >
              <Input maxLength={64} placeholder={t('orders:supplierOrders.form.manualOrderNumberPlaceholder')} />
            </Form.Item>
          ) : autoNumberPreview ? (
            <Text type="secondary" style={{ display: 'block', marginBottom: 18 }}>
              {t('orders:supplierOrders.card.more.autoNumber', { number: autoNumberPreview })}
            </Text>
          ) : null}
        </Col>
        <Col xs={24}>
          <Form.Item name="notes" label={t('orders:notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Col>
      </Row>
      {onSave && canEdit ? (
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => void save()} data-testid="supplier-order-more-data-save">
          {t('orders:supplierOrders.card.more.save')}
        </Button>
      ) : null}
    </Form>
  );

  return (
    <>
    <Collapse
      className="zd-stage__more"
      data-testid="supplier-order-more-data"
      items={[{
        key: 'more',
        label: (
          <span>
            <strong>{t('orders:supplierOrders.card.more.title')}</strong>
            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>{t('orders:supplierOrders.card.more.hint')}</Text>
          </span>
        ),
        children: content,
        forceRender: true,
      }]}
    />
    <FactoryFormModal
      open={factoryOpen}
      factory={null}
      submitting={factorySubmitting}
      onSubmit={(payload) => { void addFactory(payload); }}
      onCancel={() => setFactoryOpen(false)}
    />
    </>
  );
};

export default SupplierOrderMoreDataSection;
